import type { RunEvent } from "../../api/types";
import { formatUsd } from "../../lib/format";

/**
 * Turns raw run events into display items for the transcript: tool calls paired
 * with their results, progress heartbeats folded into the tool they belong to,
 * sub-agent messages nested under their parent tool, and noise / unrecognised
 * events collapsed into "N hidden events" groups. Pure, so it can be unit-tested.
 */

type Obj = Record<string, unknown>;
export const isObj = (v: unknown): v is Obj => typeof v === "object" && v !== null && !Array.isArray(v);
const s = (v: unknown) => (typeof v === "string" ? v : undefined);

export function tryJson(raw: string): Obj | null {
  const t = raw.trim();
  if (!t.startsWith("{")) return null;
  try {
    const v = JSON.parse(t);
    return isObj(v) ? v : null;
  } catch {
    return null;
  }
}

export function pretty(v: unknown): string {
  if (typeof v === "string") return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

export function toolResultText(content: unknown): string {
  if (typeof content === "string") return content;
  if (Array.isArray(content)) {
    return content
      .map((c) => (isObj(c) ? (s(c.text) ?? (c.type === "image" ? "[image]" : pretty(c))) : String(c)))
      .join("\n");
  }
  return pretty(content);
}

export function summarizeInput(input: unknown): string | undefined {
  if (!isObj(input)) return undefined;
  const v = input.command ?? input.file_path ?? input.path ?? input.pattern ?? input.url ?? input.description;
  return typeof v === "string" ? v.split("\n")[0].slice(0, 120) : undefined;
}

/** The parsed JSON object for an event, if it has one. */
export function parsedOf(ev: RunEvent): Obj | null {
  return ev.parsed ?? (ev.stream === "stdout" ? tryJson(ev.raw) : null);
}

export interface ToolItem {
  kind: "tool";
  key: string;
  id?: string;
  name: string;
  detail?: string;
  input?: unknown;
  result?: string;
  error?: boolean;
  /** No result seen yet. */
  pending: boolean;
  /** Latest tool_progress / task_progress elapsed time. */
  elapsedSeconds?: number;
  /** From system/task_started. */
  description?: string;
  /** Sub-agent messages (parent_tool_use_id) nested under this tool. */
  children: TxItem[];
}

export type TxItem =
  | { kind: "text"; key: string; role: "assistant" | "user"; text: string }
  | { kind: "thinking"; key: string; title: string; text: string }
  | ToolItem
  | { kind: "system"; key: string; text: string; error?: boolean; count: number }
  | { kind: "raw"; key: string; stream: RunEvent["stream"]; text: string }
  | { kind: "hidden"; key: string; events: RunEvent[] };

/**
 * - content: gets its own row(s)
 * - attach: folded into an existing row (progress heartbeats, Codex item.started)
 * - noise: no standalone value; collapsed into a hidden group
 * - unknown: unrecognised type; collapsed like noise so new CLI events never spam
 */
export type EventClass = "content" | "attach" | "noise" | "unknown";

const CONTENT_TYPES = new Set([
  "assistant",
  "user",
  "result",
  "item.completed",
  "thread.started",
  "turn.completed",
  "turn.failed",
  "error",
]);
const NOISE_TYPES = new Set(["rate_limit_event", "stream_event", "keep_alive", "turn.started"]);
const CONTENT_SYSTEM_SUBTYPES = new Set(["init", "compact_boundary", "api_retry"]);
const ATTACH_SYSTEM_SUBTYPES = new Set(["task_started", "task_progress"]);
const ATTACH_TYPES = new Set(["tool_progress", "item.started", "item.updated"]);

export function classify(p: Obj): EventClass {
  const kind = s(p.kind);
  if ((kind === "text" && s(p.text)) || kind === "tool_call" || kind === "tool_result") return "content";
  const type = s(p.type);
  if (type === undefined) return "unknown";
  if (type === "system") {
    const sub = s(p.subtype) ?? "";
    if (CONTENT_SYSTEM_SUBTYPES.has(sub)) return "content";
    if (ATTACH_SYSTEM_SUBTYPES.has(sub)) return "attach";
    return "noise";
  }
  if (CONTENT_TYPES.has(type)) return "content";
  if (ATTACH_TYPES.has(type)) return "attach";
  if (NOISE_TYPES.has(type)) return "noise";
  return "unknown";
}

export interface TranscriptStats {
  /** Events shown as (or merged into) their own rows. */
  content: number;
  /** Events folded into another row (heartbeats, task notices, Codex item.started). */
  folded: number;
  /** Events in "N hidden events" groups. */
  hidden: number;
}

export function buildTranscript(events: RunEvent[]): TxItem[] {
  return buildTranscriptWithStats(events).items;
}

export function buildTranscriptWithStats(events: RunEvent[]): { items: TxItem[]; stats: TranscriptStats } {
  const items: TxItem[] = [];
  const stats: TranscriptStats = { content: 0, folded: 0, hidden: 0 };
  const tools = new Map<string, ToolItem>();

  const push = (list: TxItem[], item: TxItem) => {
    const last = list[list.length - 1];
    if (item.kind === "system" && last?.kind === "system" && last.text === item.text && last.error === item.error) {
      last.count += 1;
      return;
    }
    if (item.kind === "tool" && item.id) tools.set(item.id, item);
    list.push(item);
  };
  const hide = (ev: RunEvent) => {
    stats.hidden += 1;
    const last = items[items.length - 1];
    if (last?.kind === "hidden") last.events.push(ev);
    else items.push({ kind: "hidden", key: `h${ev.seq}`, events: [ev] });
  };
  const tool = (key: string, name: string, rest: Partial<ToolItem> = {}): ToolItem => ({
    kind: "tool",
    key,
    name,
    pending: false,
    children: [],
    ...rest,
  });

  for (const ev of events) {
    const key = String(ev.seq);
    const p = parsedOf(ev);
    if (!p) {
      stats.content += 1;
      push(items, { kind: "raw", key, stream: ev.stream, text: ev.raw });
      continue;
    }
    const cls = classify(p);
    const type = s(p.type);

    if (cls === "noise" || cls === "unknown") {
      hide(ev);
      continue;
    }

    if (cls === "attach") {
      if (attach(p)) stats.folded += 1;
      else hide(ev);
      continue;
    }

    stats.content += 1;

    // Generic normalized shape (mock agent, env-agent notes).
    const kind = s(p.kind);
    if (kind === "text") {
      push(items, { kind: "text", key, role: "assistant", text: s(p.text) ?? "" });
      continue;
    }
    if (kind === "tool_call") {
      push(items, tool(key, s(p.name) ?? "tool", { detail: summarizeInput(p.input), input: p.input, pending: !!s(p.id), id: s(p.id) }));
      continue;
    }
    if (kind === "tool_result") {
      const t = s(p.id) ? tools.get(s(p.id)!) : undefined;
      const result = pretty(p.output ?? p.content);
      if (t) Object.assign(t, { result, pending: false });
      else push(items, tool(key, "result", { result }));
      continue;
    }

    // Claude Code stream-json
    if (type === "system") {
      const sub = s(p.subtype);
      if (sub === "init") {
        push(items, {
          kind: "system",
          key,
          text: `Session started${s(p.model) ? ` · ${s(p.model)}` : ""}${s(p.session_id) ? ` · ${s(p.session_id)}` : ""}`,
          count: 1,
        });
      } else if (sub === "compact_boundary") {
        push(items, { kind: "system", key, text: "Context compacted", count: 1 });
      } else {
        const attempt = typeof p.attempt === "number" ? ` (attempt ${p.attempt})` : "";
        push(items, { kind: "system", key, text: `API retry${attempt}`, error: true, count: 1 });
      }
      continue;
    }
    if (type === "assistant" || type === "user") {
      if (!isObj(p.message)) continue;
      const parentId = s(p.parent_tool_use_id);
      const list = (parentId && tools.get(parentId)?.children) || items;
      const role = type;
      const content = p.message.content;
      if (typeof content === "string") {
        push(list, { kind: "text", key, role, text: content });
      } else if (Array.isArray(content)) {
        content.forEach((c, i) => {
          if (!isObj(c)) return;
          const k = `${key}-${i}`;
          if (c.type === "text" && s(c.text)) {
            push(list, { kind: "text", key: k, role, text: s(c.text)! });
          } else if (c.type === "thinking" && s(c.thinking)) {
            push(list, { kind: "thinking", key: k, title: "thinking", text: s(c.thinking)! });
          } else if (c.type === "tool_use") {
            const id = s(c.id);
            push(list, tool(k, s(c.name) ?? "tool", { id, detail: summarizeInput(c.input), input: c.input, pending: !!id }));
          } else if (c.type === "tool_result") {
            const t = s(c.tool_use_id) ? tools.get(s(c.tool_use_id)!) : undefined;
            const result = toolResultText(c.content);
            const error = c.is_error === true;
            if (t) Object.assign(t, { result, error, pending: false });
            else push(list, tool(k, "result", { result, error }));
          } else if (c.type === "image") {
            push(list, { kind: "system", key: k, text: "[image]", count: 1 });
          }
        });
      }
      continue;
    }
    if (type === "result") {
      // The turn is over: anything still open was interrupted.
      for (const t of tools.values()) t.pending = false;
      const parts = [s(p.subtype), typeof p.num_turns === "number" ? `${p.num_turns} turns` : undefined];
      if (typeof p.total_cost_usd === "number") parts.push(formatUsd(p.total_cost_usd));
      if (typeof p.duration_ms === "number") parts.push(`${Math.round(p.duration_ms / 1000)}s`);
      push(items, { kind: "system", key, text: `Result: ${parts.filter(Boolean).join(" · ")}`, error: !!p.is_error, count: 1 });
      continue;
    }

    // Codex exec --json
    if (type === "item.completed" && isObj(p.item)) {
      const id = s(p.item.id);
      const item = codexItem(key, p.item, false);
      const existing = id ? tools.get(`codex:${id}`) : undefined;
      if (existing && item.kind === "tool") Object.assign(existing, { ...item, key: existing.key, children: existing.children });
      else push(items, item);
      continue;
    }
    if (type === "thread.started") {
      push(items, { kind: "system", key, text: `Thread ${s(p.thread_id) ?? ""}`, count: 1 });
      continue;
    }
    if (type === "turn.completed") {
      const u = isObj(p.usage) ? p.usage : {};
      const usage = typeof u.input_tokens === "number" ? ` · ${u.input_tokens} in / ${u.output_tokens} out` : "";
      push(items, { kind: "system", key, text: `Turn completed${usage}`, count: 1 });
      continue;
    }
    if (type === "turn.failed" || type === "error") {
      push(items, { kind: "system", key, text: pretty(p.error ?? p.message ?? p), error: true, count: 1 });
      continue;
    }
  }
  return { items, stats };

  /** Fold a progress-style event into the row it belongs to. False if there is none. */
  function attach(p: Obj): boolean {
    const type = s(p.type);
    if (type === "tool_progress") {
      const own = s(p.tool_use_id)?.replace(/-heartbeat-\d+$/, "");
      const t = (own && tools.get(own)) || (s(p.parent_tool_use_id) && tools.get(s(p.parent_tool_use_id)!));
      if (!t) return false;
      if (typeof p.elapsed_time_seconds === "number") t.elapsedSeconds = p.elapsed_time_seconds;
      return true;
    }
    if (type === "system") {
      const t = s(p.tool_use_id) ? tools.get(s(p.tool_use_id)!) : undefined;
      if (!t) return false;
      if (s(p.description)) t.description = s(p.description);
      const usage = isObj(p.usage) ? p.usage : undefined;
      const ms = usage && typeof usage.duration_ms === "number" ? usage.duration_ms : undefined;
      if (ms !== undefined) t.elapsedSeconds = Math.round(ms / 1000);
      if (typeof p.elapsed_time_seconds === "number") t.elapsedSeconds = p.elapsed_time_seconds;
      return true;
    }
    // Codex item.started / item.updated: show the running item, filled in by item.completed.
    if (!isObj(p.item) || !s(p.item.id)) return false;
    const id = `codex:${s(p.item.id)}`;
    const item = codexItem(id, p.item, true);
    if (item.kind !== "tool") return false;
    const existing = tools.get(id);
    if (existing) Object.assign(existing, { ...item, key: existing.key, children: existing.children });
    else {
      item.id = id;
      push(items, item);
    }
    return true;
  }

  function codexItem(key: string, it: Obj, pending: boolean): TxItem {
    switch (s(it.type)) {
      case "agent_message":
        return { kind: "text", key, role: "assistant", text: s(it.text) ?? "" };
      case "reasoning":
        return { kind: "thinking", key, title: "reasoning", text: s(it.text) ?? "" };
      case "command_execution": {
        const code = typeof it.exit_code === "number" ? it.exit_code : undefined;
        return tool(key, "shell", {
          detail: `${s(it.command) ?? ""}${code !== undefined ? ` → ${code}` : ""}`,
          result: s(it.aggregated_output),
          error: code !== undefined && code !== 0,
          pending,
        });
      }
      case "file_change": {
        const changes = Array.isArray(it.changes) ? it.changes : [];
        const list = changes.map((c) => (isObj(c) ? `${s(c.kind) ?? ""} ${s(c.path) ?? ""}` : "")).join("\n");
        return tool(key, "file change", { detail: `${changes.length} file(s)`, result: list, pending });
      }
      case "mcp_tool_call":
        return tool(key, `${s(it.server) ?? "mcp"}.${s(it.tool) ?? "tool"}`, {
          result: pending ? undefined : pretty({ arguments: it.arguments, result: it.result, error: it.error }),
          error: !!it.error,
          pending,
        });
      default:
        return tool(key, s(it.type) ?? "item", { result: pretty(it), pending });
    }
  }
}

/** "tool_progress ×4, system/task_started ×1" for a hidden group. */
export function hiddenBreakdown(events: RunEvent[]): string {
  const counts = new Map<string, number>();
  for (const ev of events) {
    const p = parsedOf(ev);
    const type = p ? (s(p.type) ?? s(p.kind) ?? "object") : "line";
    const label = p && type === "system" && s(p.subtype) ? `system/${s(p.subtype)}` : type;
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return [...counts].map(([label, n]) => `${label} ×${n}`).join(", ");
}

export function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const sec = Math.round(seconds % 60);
  return m ? `${m}m ${sec}s` : `${sec}s`;
}
